// Browser mint lifted from tools/build/trap-disarm-demo-build.mjs (mechanical and rune bench).
// The same seeded stream is used by both games; production seeds come only from Web Crypto.
import type { MechGeometry, RuneGeometry, RuneDefinition, RuneStroke, RuneWard, RuneTensionModel } from './board-types.js';
import { phpRound } from '../pl/php-round.js';
type Point = { x: number; y: number };
const SEAL_OUTER = 3.6;
const round2 = (n: number): number => phpRound(n, 2);
const round4 = (n: number): number => phpRound(n, 4);
/** Mulberry32; accepting a seed makes captures and parity runs repeatable without server dice. */
export function seededDisarmRandom(seed: number): () => number {
  return () => {
    let t = seed += 0x6D2B79F5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}
export function browserDisarmRandom(): () => number {
  return seededDisarmRandom(crypto.getRandomValues(new Uint32Array(1))[0]!);
}
export function tierFromDc(dc: number): number { return Math.max(1, Math.min(6, Math.round((dc - 8) / 4))); }
// ft_trap_minigame_skill_edge
export function skillEdge(casterTier: number, trapTier: number): number { return Math.max(0, casterTier) - trapTier; }

// ── mechanical ladders ─────────────────────────────────────────────────────────────────────────
export function searCount(t: number): number { return Math.min(7, t + 1); }
export function catchWidth(t: number): number {
  if(t <= 1) return 16.0; if(t === 2) return 12.0; if(t === 3) return 9.0;
  if(t === 4) return 7.0; if(t === 5) return 5.0; return 3.5;
}
export function feelRadius(e: number): number {
  if(e <= -5) return 4.0; if(e === -4) return 7.0; if(e === -3) return 12.0; if(e === -2) return 20.0;
  if(e === -1) return 30.0; if(e === 0) return 45.0; if(e === 1) return 60.0; if(e === 2) return 75.0;
  return 90.0;
}
export function strainSeconds(e: number): number {
  if(e <= -5) return 0.35; if(e === -4) return 0.50; if(e === -3) return 0.70; if(e === -2) return 1.00;
  if(e === -1) return 1.40; if(e === 0) return 1.90; if(e === 1) return 2.50; if(e === 2) return 3.20;
  return 4.00;
}
// ft_trap_minigame_mint_mechanical — centers stay in [25, 82] so there is always a searchable
// run-up below the catch and an overshoot band above it (the needle's red-feeder).
export function mintMechanical(trapTier: number, edge: number, rng: () => number = browserDisarmRandom()): MechGeometry {
  var width = catchWidth(trapTier), sears = [], count = searCount(trapTier);
  for(var i = 0; i < count; i++){
    var center = 25.0 + (Math.floor(rng() * 5701) / 100.0);
    sears.push({ zone_lo: round2(center - width / 2), zone_hi: round2(center + width / 2) });
  }
  var geo = { sears: sears, feel_radius: feelRadius(edge), strain_seconds: strainSeconds(edge) };
  var model = tensionModel(trapTier, edge);
  return { ...geo, ...model };
}
// ft_trap_minigame_tension_factor / ft_trap_minigame_tension_model — the 2026-08-04 rework. The
// needle is the WHOLE TRAP's agitation now, not one sear's: a LIVE component that answers the
// moment you move a handle and bleeds off when you stop, over a FLOOR that ratchets up on fumbles
// and sawing and comes down only by seating a sear. Difficulty scales the RATES, never the starting
// value — a trap that started pre-tensioned would eat the PERFECT budget before the player acted.
export function tensionFactor(trapTier: number, edge: number): number {
  var tier = trapTier <= 1 ? 0.75 : trapTier === 2 ? 0.85 : trapTier === 3 ? 0.95
           : trapTier === 4 ? 1.05 : trapTier === 5 ? 1.15 : 1.25;
  var ed = edge <= -5 ? 1.45 : edge === -4 ? 1.32 : edge === -3 ? 1.20 : edge === -2 ? 1.10
         : edge === -1 ? 1.05 : edge === 0 ? 1.00 : edge === 1 ? 0.90 : edge === 2 ? 0.78 : 0.68;
  return tier * ed;
}
// ft_trap_minigame_tension_rate — a live-gain rate across D, TAPERED rather than clamped.
//
// The ceiling is not free: at tier 6 / edge -5 a careful player already peaks at ~52 of the 55
// PERFECT line, so the top cannot rise. The 2026-08-04 "gain 50% faster" pass raises the MIDDLE
// toward a fixed ceiling — but it must do so without collapsing the top. Simply scaling the
// coefficient and clamping drove every tier-6 board from edge -5 through edge 0 onto ONE rate, so a
// matched expert and a novice five tiers under became tension-identical; the other ladders do not
// cover for that, since strain_seconds only bites above 80 and cannot separate PERFECT outcomes at
// all. Proportional below the anchor, gentler slope above, ceiling exactly at the hardest board.
export function tensionRate(d: number, slope: number, min: number, max: number): number {
  var anchor = 0.95, top = 1.8125;
  var atAnchor = slope * anchor;
  var rate = d <= anchor
    ? slope * d
    : atAnchor + (d - anchor) * ((max - atAnchor) / (top - anchor));
  return Math.max(min, Math.min(max, rate));
}
export function tensionModel(trapTier: number, edge: number) {
  var d = tensionFactor(trapTier, edge);
  var fumble = round4(Math.max(3.5, Math.min(11.0, 7.0 * d)));
  return {
    // ⛔ Creep per SECOND while any sear is held, moving or not — the trap does not relax in your
    // hand. Weighted heavier than travel: the risk should come from holding the mechanism open,
    // not from how far the geometry happened to make you move a handle.
    hold_live_per_second: round4(tensionRate(d, 2.25, 1.7, 3.2)),
    move_live_per_unit: round4(tensionRate(d, 0.2185, 0.14, 0.24)),
    ratchet_per_fumble: fumble,
    ratchet_per_excess_unit: round4(Math.max(0.03, Math.min(0.12, 0.08 * d))),
    free_excess_per_drag: 6.0,
    floor_relief_per_seat: round4(fumble * 0.60),
    live_retention_on_seat: 0.20,
    overshoot_rate: 2.2,
    live_decay_rate: 30.0,
    live_decay_delay: 0.20
  };
}
export function runeAnchors(): Point[] {
  var pts = [{ x: 0.5, y: 0.5 }];
  for(var i = 0; i < 8; i++){
    var a = (i / 8) * Math.PI * 2 - Math.PI / 2;
    pts.push({ x: 0.5 + Math.cos(a) * 0.34, y: 0.5 + Math.sin(a) * 0.34 });
  }
  return pts;
}
export function runeStrokeCount(tier: number): number { return tier <= 1 ? 1 : tier <= 3 ? 2 : tier <= 5 ? 3 : 4; }
export function runeCount(tier: number): number { return tier <= 2 ? 1 : tier <= 4 ? 2 : 3; }
export function runeWardCount(tier: number): number { return Math.max(0, Math.min(5, tier - 1)); }
// Order only matters on the nastier traps — and even then the skill ladder decides how loudly the
// next stroke announces itself. This is the light echo of the weave: a little magical READING kept
// alive without rebuilding its whole topological burden.
export function runeOrderMatters(tier: number): boolean { return tier >= 4; }

// Collision corridor half-width in normalized units. Even the worst edge must clear ordinary hand
// jitter: a negative edge should cost you INFORMATION and margin, never demand pixel-perfection.
export function runeCorridor(edge: number): number {
  var e = Math.max(-5, Math.min(3, edge));
  return [0.030, 0.033, 0.036, 0.040, 0.045, 0.052, 0.060, 0.068, 0.078][e + 5]!;
}
// How strongly the blade is pulled onto the true path, and from how far.
export function runeGuidance(edge: number) {
  var e = Math.max(-5, Math.min(3, edge));
  return { pull: [0.10, 0.14, 0.19, 0.25, 0.32, 0.42, 0.55, 0.68, 0.80][e + 5]!,
           capture: [0.045, 0.050, 0.056, 0.064, 0.074, 0.086, 0.100, 0.118, 0.140][e + 5]! };
}
// Ward sensing: how far out a ward is felt, and how clearly it renders once felt. clarity 0 = a
// vague disturbance you must interpret; 1 = footprint and safe bypass drawn for you.
export function runeWardSense(edge: number) {
  var e = Math.max(-5, Math.min(3, edge));
  return { radius: [0.055, 0.065, 0.080, 0.100, 0.125, 0.155, 0.195, 0.240, 0.300][e + 5]!,
           clarity: [0.10, 0.18, 0.28, 0.40, 0.55, 0.70, 0.84, 0.94, 1.00][e + 5]! };
}

// Tension rates for the rune board. Same floor+live architecture as the mechanical game, with ONE
// deliberate difference: the blade-down creep is MILD. On the mechanical board holding is a choice,
// so charging for it is fair; here sustained contact is the entire interaction, so a steep rate
// would be a raw speed test and would punish anyone with a tremor. Off-corridor travel, wards and
// misreads carry the tension budget instead — the things the player actually chooses.
export function runeTensionModel(trapTier: number, edge: number): RuneTensionModel {
  var d = tensionFactor(trapTier, edge);
  return {
    down_live_per_second: round4(1.05 * d),      // mild background pressure while the blade is down
    off_live_per_second: round4(9.0 * d),        // the real cost: tracing outside the corridor
    ward_live_pulse: round4(16.0 * d),           // touching an armed ward
    ward_floor_ratchet: round4(7.0 * d),         // …and it leaves a permanent mark
    seal_live_per_second: round4(6.0 * d),       // sealing a ward is expensive but chosen
    misread_floor_ratchet: round4(5.0 * d),      // starting the wrong stroke when order matters
    rune_floor_relief: round4(6.0 * d),
    live_retention_on_rune: 0.20,
    strain_seconds: strainSeconds(edge)
  };
}

// Mint one rune: a connected run of strokes over the anchor lattice, plus wards sitting ON the
// path (which is the point — a ward you can simply walk around is not a hazard).
export function mintRune(trapTier: number, edge: number, rng: () => number): RuneDefinition {
  var A = runeAnchors();
  var nStrokes = runeStrokeCount(trapTier);
  var strokes = [];
  var used: Record<string, boolean> = {};
  var cursor = 1 + Math.floor(rng() * 8);
  for(var s = 0; s < nStrokes; s++){
    var pts = [];
    var hops = 2 + Math.floor(rng() * 2);
    var at = s === 0 ? cursor : (1 + Math.floor(rng() * 8));
    pts.push(A[at]!);
    for(var h = 0; h < hops; h++){
      var next;
      var guard = 0;
      do {
        next = Math.floor(rng() * 9);
        guard++;
      } while((next === at || used[at + '-' + next]) && guard < 24);
      used[at + '-' + next] = true; used[next + '-' + at] = true;
      // Half the hops bow into an arc so the rune does not read as a wire diagram.
      if(rng() < 0.45){
        var m = { x: (A[at]!.x + A[next]!.x) / 2, y: (A[at]!.y + A[next]!.y) / 2 };
        var nx = -(A[next]!.y - A[at]!.y), ny = (A[next]!.x - A[at]!.x);
        var len = Math.hypot(nx, ny) || 1;
        var bow = (rng() < 0.5 ? 1 : -1) * 0.10;
        var c = { x: m.x + (nx / len) * bow, y: m.y + (ny / len) * bow };
        for(var q = 1; q <= 8; q++){
          var t = q / 8, it = 1 - t;
          pts.push({ x: it * it * A[at]!.x + 2 * it * t * c.x + t * t * A[next]!.x,
                     y: it * it * A[at]!.y + 2 * it * t * c.y + t * t * A[next]!.y });
        }
      } else {
        pts.push(A[next]!);
      }
      at = next;
    }
    strokes.push({ pts: pts, len: polyLength(pts) });
  }
  // Wards along the traced path, never within a whisker of a stroke end (an unavoidable ward on a
  // start anchor is a toll, not a decision).
  var wards: RuneWard[] = [];
  var nW = runeWardCount(trapTier);
  for(var w = 0; w < nW; w++){
    // ⚠ Wards are placed APART. Overlapping working bands make sealing unreliable — you circle one
    // and drift into the next one's band — and two hazards you cannot address separately is not a
    // decision, it is a wall. Rejected placements just retry; the count is a target, not a promise.
    for(var attempt = 0; attempt < 20; attempt++){
      var si = Math.floor(rng() * strokes.length);
      var t = 0.18 + rng() * 0.64;
      var rr = 0.030 + rng() * 0.014;
      var p = pointAlong(strokes[si]!, t);
      var clash = false;
      for(var e = 0; e < wards.length; e++){
        var ep = pointAlong(strokes[wards[e]!.stroke]!, wards[e]!.t);
        if(Math.hypot(p.x - ep.x, p.y - ep.y) < (rr + wards[e]!.r) * SEAL_OUTER * 0.9){ clash = true; break; }
      }
      if(!clash){
        wards.push({ stroke: si, t: t, r: rr, state: 'armed' });
        break;
      }
    }
  }
  return { strokes: strokes, wards: wards, orderMatters: runeOrderMatters(trapTier) };
}
export function polyLength(pts: Point[]): number {
  var L = 0;
  for(var i = 1; i < pts.length; i++) L += Math.hypot(pts[i]!.x - pts[i-1]!.x, pts[i]!.y - pts[i-1]!.y);
  return L;
}
export function pointAlong(stroke: RuneStroke, t: number): Point {
  var target = stroke.len * Math.max(0, Math.min(1, t)), acc = 0;
  for(var i = 1; i < stroke.pts.length; i++){
    var seg = Math.hypot(stroke.pts[i]!.x - stroke.pts[i-1]!.x, stroke.pts[i]!.y - stroke.pts[i-1]!.y);
    if(acc + seg >= target){
      var f = seg > 0 ? (target - acc) / seg : 0;
      return { x: stroke.pts[i-1]!.x + (stroke.pts[i]!.x - stroke.pts[i-1]!.x) * f,
               y: stroke.pts[i-1]!.y + (stroke.pts[i]!.y - stroke.pts[i-1]!.y) * f };
    }
    acc += seg;
  }
  return stroke.pts[stroke.pts.length - 1]!;
}
export function mintRuneBoard(trapTier: number, edge: number, rng: () => number = browserDisarmRandom()): RuneGeometry {

  var runes = [];
  var n = runeCount(trapTier);
  for(var i = 0; i < n; i++) runes.push(mintRune(trapTier, edge, rng));
  var model = runeTensionModel(trapTier, edge);
  return { board: 'rune', seal_outer: SEAL_OUTER, runes: runes, corridor: runeCorridor(edge), guidance: runeGuidance(edge),
           ward_sense: runeWardSense(edge), tension: model, strain_seconds: model.strain_seconds };
}

